import { Tabs } from 'common/constants';
import { LockStatus } from 'pages/AuthorizePaymentPage/types';
import { FLOAT_REGEX } from './constants';
import type {
  InvoiceDataType,
  VendorDataType,
  VendorsResponseDataType,
  VendorsTableDataType
} from './types';
import type { IAggFuncParams, IRowNode } from 'ag-grid-community';

export const getNumberFromCurrency = (currency?: string): number =>
  Number(currency?.match(FLOAT_REGEX)?.join('')) || 0;

export const getVendorsTableDataId = (
  vendor: VendorDataType,
  invoice: InvoiceDataType
): string => `${vendor.vendorNo}-${invoice.invoiceId}`;

export const getTableRowData = (
  data: VendorDataType[] | undefined | null,
  searchInputValue: string
): VendorsTableDataType[] => {
  const query = searchInputValue.toLowerCase();
  return (
    data?.flatMap(({ onHold, ...vendor }: VendorDataType) =>
      vendor.invoices?.reduce(
        (acc: VendorsTableDataType[], invoice: InvoiceDataType) => {
          if (
            vendor.vendorNo?.toLowerCase().includes(query) ||
            vendor.vendorName?.toLowerCase().includes(query) ||
            invoice.invoiceId?.toLowerCase().includes(query) ||
            invoice.amount?.toLowerCase().includes(query) ||
            invoice.discountAmount?.toLowerCase().includes(query) ||
            invoice.netAmount?.toLowerCase().includes(query) ||
            invoice.invoiceDate?.toLowerCase().includes(query) ||
            invoice.dueDate?.toLowerCase().includes(query)
          ) {
            const rowId = getVendorsTableDataId(vendor, invoice);
            return [
              ...acc,
              {
                ...vendor,
                ...invoice,
                id: rowId,
                vendorOnHold: onHold || false,
                lockStatus: invoice.lockStatus || LockStatus.UNLOCKED
              }
            ];
          }
          return acc;
        },
        []
      )
    ) || []
  );
};

export const getVendorsTableRowData = (
  data: VendorsResponseDataType | null,
  searchInputValue: string
): VendorsTableDataType[] => getTableRowData(data?.vendors, searchInputValue);

export const getRowNodeData = (node?: IRowNode): VendorsTableDataType | null =>
  (node?.data as VendorsTableDataType) || null;

export const getFirstChildData = ({
  rowNode
}: IAggFuncParams): VendorsTableDataType | null => {
  const node = rowNode?.allLeafChildren?.[0];
  return getRowNodeData(node);
};

export const netAmountComparator = (
  a: VendorsTableDataType,
  b: VendorsTableDataType
): number =>
  getNumberFromCurrency(a.netAmount) > getNumberFromCurrency(b.netAmount)
    ? -1
    : 1;

export const getInvoiceIdsToSelect = (
  invoices: VendorsTableDataType[],
  checked: boolean,
  selectedTab: string
): string[] => {
  if (!checked) return invoices.map(({ id }) => id);
  const rowsToSelect: string[] = [];
  const sortedInvoices = [...invoices].sort(netAmountComparator);
  let acc = 0;
  sortedInvoices.forEach(child => {
    const netAmount = getNumberFromCurrency(child.netAmount);
    if (
      netAmount + acc >= 0 &&
      !child.onHold &&
      !child.vendorOnHold &&
      child.id &&
      (selectedTab === Tabs.NeedsAuthorization ||
        child.lockStatus !== LockStatus.LOCKED)
    ) {
      rowsToSelect.push(child.id);
      acc += netAmount;
    }
  });
  return rowsToSelect;
};

export const sortDollarAmounts = (
  firstAmount: string,
  secondAmount: string
): number =>
  getNumberFromCurrency(firstAmount) - getNumberFromCurrency(secondAmount);
